import { create } from "zustand";
import type { Card, DrawMode, GameState, PileRef } from "@solitaire/game-engine";
import { GAME_CONFIG } from "../config.js";
import {
  applyMove,
  canAutoComplete,
  canPlaceOnFoundation,
  canPlaceOnTableau,
  canPlaceSequenceOnTableau,
  createInitialGameState,
  findAutoAceMove,
  findAutoCompleteMove,
  FOUNDATION_SUIT_ORDER,
  generateSeed,
  drawFromStock as engineDraw,
  recordShake,
  undoLastMove,
} from "@solitaire/game-engine";
import { logger } from "@solitaire/logger";
import { dragState } from "../lib/dragState.js";

interface FlyMoveOptions {
  /** Sequentielle Batches: z.B. [[backId1,backId2],[flipId]] — Batch 0 fliegt zuerst, dann Batch 1. */
  batches?: string[][];
  /** Welche Speed-Config soll verwendet werden? */
  speedType?: "default" | "stock" | "victory";
}

interface FlyCallbacks {
  onBeforeMove: (cardIds: string[], options?: FlyMoveOptions) => void;
  onAfterMove: () => void;
}

interface GameStore {
  gameState: GameState;
  /** Aktuell gespieltes Set (1-basiert). Seed = Set-Nummer. */
  currentSetNumber: number;
  selectedCards: readonly Card[] | null;
  selectedFromPile: PileRef | null;
  /** Läuft gerade ein automatischer Abschluss? */
  isAutoCompleting: boolean;
  /** Automatisches Ass aktiviert? */
  autoAce: boolean;
  setAutoAce: (v: boolean) => void;
  /** Fly-Animation Callbacks (registriert vom FlyAnimationContext). */
  _flyCallbacks: FlyCallbacks | null;
  setFlyCallbacks: (cbs: FlyCallbacks | null) => void;

  /** Aktuelles Set neu starten. */
  retrySet: () => void;
  /** Zum nächsten Set wechseln (currentSetNumber + 1, ab Set 1 wenn kein Set aktiv). */
  nextSet: () => void;
  drawMode: DrawMode;
  newGame: (seed?: number, drawMode?: DrawMode) => void;
  drawStock: () => void;
  handleTableauCardClick: (columnIndex: number, cardIndex: number) => void;
  handleEmptyTableauClick: (columnIndex: number) => void;
  handleWasteClick: () => void;
  handleFoundationClick: (foundationIndex: number) => void;
  clearSelection: () => void;
  undo: () => void;
  tick: () => void;
  /** Drag & Drop: versucht die gezogene Karte auf toTarget abzulegen.
   *  Gibt true zurück wenn der Zug gültig war, sonst false. */
  applyDrop: (toTarget: string) => boolean;
  /** Startet den automatischen Abschluss wenn alle Karten sichtbar sind. */
  startAutoComplete: () => void;
  /** ID der Karte, die gerade shaken soll (kein gültiger Zug). */
  shakeCardId: string | null;
  /** Journey-Init bereits gelaufen? Verhindert erneutes Laden aus DB nach Remount (z.B. Leaderboard → zurück). */
  _journeyInitDone: boolean;
  /** Höchstes erreichtes Set (für Set-Picker). Überlebt Remounts. */
  _maxReachedSet: number;
  /** Splash-Screen bereits angezeigt? Wird bei newGame zurückgesetzt. */
  _splashDone: boolean;
}

/** Seed aus Set-Nummer: Set N = Seed N. */
export function setNumberToSeed(setNumber: number): number {
  return setNumber;
}

/** Liest ?seed= aus der URL — für Share-Links. */
function getSeedFromUrl(): number | undefined {
  try {
    const raw = new URLSearchParams(window.location.search).get("seed");
    if (!raw) return undefined;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) && n >= 0 ? (n >>> 0) : undefined;
  } catch {
    return undefined;
  }
}

/** Liest ?set= aus der URL — Set-Nummer (1-basiert). */
function getSetFromUrl(): number | undefined {
  try {
    const raw = new URLSearchParams(window.location.search).get("set");
    if (!raw) return undefined;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) && n >= 1 ? n : undefined;
  } catch {
    return undefined;
  }
}

function createGame(seed?: number, drawMode: DrawMode = 1): GameState {
  const s = seed ?? generateSeed();
  return createInitialGameState("game-" + Date.now(), s, drawMode);
}

/** Initialer Set beim Start: ?set=N > ?seed=X (Seed = Set-Nummer) > Set 1. */
function getInitialSetAndSeed(): { setNumber: number; seed: number } {
  const setFromUrl = getSetFromUrl();
  if (setFromUrl !== undefined) {
    return { setNumber: setFromUrl, seed: setFromUrl };
  }
  const seedFromUrl = getSeedFromUrl();
  if (seedFromUrl !== undefined && seedFromUrl >= 1) {
    // Seed = Set-Nummer (da setNumberToSeed(n) = n)
    return { setNumber: seedFromUrl, seed: seedFromUrl };
  }
  // Standard: Set 1
  return { setNumber: 1, seed: 1 };
}

const _initial = getInitialSetAndSeed();

export const useGameStore = create<GameStore>((set, get) => {
  /** Fly-Animation vor/nach State-Update auslösen. */
  function flyMove(cardIds: string[], doSet: () => void, options?: FlyMoveOptions): void {
    const cbs = get()._flyCallbacks;
    if (cbs) cbs.onBeforeMove(cardIds, options);
    doSet();
    if (cbs) cbs.onAfterMove();
  }

  /**
   * Interner Helper: Prüft ob in `afterState` ein Ass sichtbar ist und
   * schedulet dessen automatischen Flug auf die Foundation.
   * Wird nach jedem erfolgreichen Spielzug aufgerufen.
   */
  function scheduleAutoAce(afterState: GameState): void {
    if (!get().autoAce) return; // Auto-Ace deaktiviert
    if (!findAutoAceMove(afterState)) return; // kein Ass sichtbar → sofort abbrechen
    setTimeout(() => {
      const { gameState, isAutoCompleting } = get();
      if (isAutoCompleting) return; // Auto-Complete übernimmt
      const move = findAutoAceMove(gameState);
      if (!move) return;
      const result = applyMove(gameState, move);
      if (result.ok) {
        const aceCard = (move as { card?: { id?: string } }).card;
        if (aceCard?.id) {
          flyMove([aceCard.id], () =>
            set({ gameState: result.value, selectedCards: null, selectedFromPile: null }));
        } else {
          set({ gameState: result.value, selectedCards: null, selectedFromPile: null });
        }
        logger.info("Auto-Ass → Foundation", { suit: (move as { card?: { suit?: string } }).card?.suit });
        if (canAutoComplete(result.value)) get().startAutoComplete();
        else scheduleAutoAce(result.value); // weitere Asse prüfen
      }
    }, GAME_CONFIG.autoAceDelayMs);
  }

  /**
   * Auto-Move: Zählt alle gültigen Ziele für die Karten.
   * - Genau 1 Ziel → bewege dorthin.
   * - 0 Ziele → Shake + Punktabzug.
   * - 2+ Ziele → nichts (User muss per Drag entscheiden).
   */
  function autoMove(fromPile: PileRef, cards: readonly Card[]): void {
    const { gameState } = get();
    const cardDesc = cards.map((c) => `${c.rank} of ${c.suit}`).join(", ");
    console.log(`[autoMove] KLICK: ${cardDesc} from ${fromPile.kind}${fromPile.kind === "tableau" ? `[${fromPile.columnIndex}]` : ""}`);

    type Target =
      | { kind: "foundation"; index: number }
      | { kind: "tableau"; index: number };
    const targets: Target[] = [];

    // Foundation-Ziele (nur Einzelkarte)
    if (cards.length === 1) {
      const card = cards[0]!;
      for (let fi = 0; fi < 4; fi++) {
        const fp = gameState.foundation[fi]!;
        if (canPlaceOnFoundation(card, fp, FOUNDATION_SUIT_ORDER[fi]!)) {
          targets.push({ kind: "foundation", index: fi });
          console.log(`[autoMove]   ✓ Foundation[${fi}] möglich`);
        }
      }
    }

    // Tableau-Ziele
    for (let ti = 0; ti < 7; ti++) {
      if (fromPile.kind === "tableau" && fromPile.columnIndex === ti) continue;
      const targetCol = gameState.tableau[ti]!;
      const valid =
        cards.length === 1
          ? canPlaceOnTableau(cards[0]!, targetCol)
          : canPlaceSequenceOnTableau(cards, targetCol);
      if (valid) {
        targets.push({ kind: "tableau", index: ti });
        const topCard = targetCol[targetCol.length - 1];
        console.log(`[autoMove]   ✓ Tableau[${ti}] möglich (top: ${topCard ? `${topCard.rank} of ${topCard.suit}` : "leer"})`);
      }
    }

    console.log(`[autoMove]   Ziele gesamt: ${targets.length}`);

    // 0 Ziele → Shake + Punktabzug
    if (targets.length === 0) {
      console.log(`[autoMove]   → SHAKE (keine Ziele)`);
      const cardId = cards[0]?.id;
      if (cardId) {
        set({ gameState: recordShake(gameState), shakeCardId: cardId });
        setTimeout(() => {
          if (get().shakeCardId === cardId) set({ shakeCardId: null });
        }, 500);
      }
      return;
    }

    // Asse → Foundation hat IMMER Priorität (auch bei 2+ Zielen)
    const foundationTarget = targets.find((t) => t.kind === "foundation");
    if (cards.length === 1 && cards[0]!.rank === 1 && foundationTarget) {
      console.log(`[autoMove]   → ACE-Priorität: Foundation`);
    } else if (targets.length > 1) {
      // 2+ Ziele → Shake OHNE Punktabzug, User muss per Drag entscheiden
      console.log(`[autoMove]   → SHAKE (${targets.length} Ziele, User muss draggen, kein Punktabzug)`);
      const cardId = cards[0]?.id;
      if (cardId) {
        set({ shakeCardId: cardId });
        setTimeout(() => {
          if (get().shakeCardId === cardId) set({ shakeCardId: null });
        }, 500);
      }
      return;
    }

    // Genau 1 Ziel oder Ass → Auto-Move
    const target = foundationTarget ?? targets[0]!;
    if (target.kind === "foundation") {
      const card = cards[0]!;
      const result = applyMove(gameState, {
        type: "MOVE_TO_FOUNDATION",
        card,
        fromPile,
        toFoundationIndex: target.index,
      });
      if (result.ok) {
        flyMove([card.id], () =>
          set({ gameState: result.value, selectedCards: null, selectedFromPile: null }));
        logger.debug("Auto-Move → Foundation", { suit: card.suit, rank: card.rank });
        if (canAutoComplete(result.value)) get().startAutoComplete();
        else scheduleAutoAce(result.value);
      }
    } else {
      const result = applyMove(gameState, {
        type: "MOVE_TO_TABLEAU",
        cards,
        fromPile,
        toColumnIndex: target.index,
      });
      if (result.ok) {
        flyMove(cards.map((c) => c.id), () =>
          set({ gameState: result.value, selectedCards: null, selectedFromPile: null }));
        logger.debug("Auto-Move → Tableau", { to: target.index });
        if (canAutoComplete(result.value)) get().startAutoComplete();
        else scheduleAutoAce(result.value);
      }
    }
  }

  return {
  gameState: createGame(_initial.seed),
  currentSetNumber: _initial.setNumber,
  selectedCards: null,
  selectedFromPile: null,
  isAutoCompleting: false,
  shakeCardId: null,
  _journeyInitDone: false,
  _maxReachedSet: _initial.setNumber,
  _splashDone: false,
  drawMode: 1 as DrawMode,
  autoAce: false,
  setAutoAce: (v) => set({ autoAce: v }),
  _flyCallbacks: null,
  setFlyCallbacks: (cbs) => set({ _flyCallbacks: cbs }),

  retrySet: () => {
    const { currentSetNumber, drawMode } = get();
    const gameState = createGame(currentSetNumber, drawMode);
    logger.info("Set nochmal starten", { setNumber: currentSetNumber, seed: gameState.seed });
    set({ gameState, selectedCards: null, selectedFromPile: null, isAutoCompleting: false });
  },

  nextSet: () => {
    const { currentSetNumber, _maxReachedSet, drawMode } = get();
    const next = currentSetNumber + 1;
    const gameState = createGame(next, drawMode);
    logger.info("Nächstes Set", { setNumber: next, seed: gameState.seed });
    set({ gameState, currentSetNumber: next, _maxReachedSet: Math.max(_maxReachedSet, next), selectedCards: null, selectedFromPile: null, isAutoCompleting: false });
  },

  newGame: (seed, newDrawMode) => {
    const { _maxReachedSet } = get();
    const dm = newDrawMode ?? get().drawMode;
    const gameState = createGame(seed, dm);
    const setNumber = seed ?? gameState.seed;
    logger.info("Neues Spiel gestartet", { seed: gameState.seed, drawMode: dm });
    set({ gameState, currentSetNumber: setNumber, drawMode: dm, _maxReachedSet: Math.max(_maxReachedSet, setNumber), selectedCards: null, selectedFromPile: null, isAutoCompleting: false, _splashDone: false });
  },

  drawStock: () => {
    const { gameState, drawMode } = get();
    const stock = gameState.stock;

    // Stock leer → Recycling, kein Fly
    if (stock.length === 0) {
      const result = engineDraw(gameState);
      if (result.ok) {
        set({ gameState: result.value, selectedCards: null, selectedFromPile: null });
        if (canAutoComplete(result.value)) get().startAutoComplete();
        else scheduleAutoAce(result.value);
      } else {
        logger.warn("drawStock fehlgeschlagen", { error: result.error });
      }
      return;
    }

    const count = Math.min(drawMode, stock.length);
    const drawnCards = stock.slice(-count);
    const allIds = drawnCards.map((c) => c.id);

    const result = engineDraw(gameState);
    if (result.ok) {
      const doSet = () =>
        set({ gameState: result.value, selectedCards: null, selectedFromPile: null });

      if (count > 1) {
        // Draw-3: Jede Karte einzeln nacheinander animieren
        // z.B. 3 Karten → [[id1], [id2], [id3]] — Rücken, Rücken, Flip
        const batches = drawnCards.map((c) => [c.id]);
        flyMove(allIds, doSet, { batches, speedType: "stock" });
      } else {
        // Draw-1: einzelne Karte mit Flip
        flyMove(allIds, doSet, { speedType: "stock" });
      }
      if (canAutoComplete(result.value)) get().startAutoComplete();
      else scheduleAutoAce(result.value);
    } else {
      logger.warn("drawStock fehlgeschlagen", { error: result.error });
    }
  },

  clearSelection: () => set({ selectedCards: null, selectedFromPile: null }),

  handleTableauCardClick: (columnIndex, cardIndex) => {
    const { gameState } = get();
    const column = gameState.tableau[columnIndex];
    if (!column) return;
    const card = column[cardIndex];
    if (!card) return;
    console.log(`[handleTableauCardClick] col=${columnIndex} idx=${cardIndex} card=${card.rank} of ${card.suit} faceUp=${card.faceUp}`);

    // Verdeckte oberste Karte → aufdecken
    if (!card.faceUp && cardIndex === column.length - 1) {
      const result = applyMove(gameState, {
        type: "FLIP_CARD",
        tableauColumnIndex: columnIndex,
      });
      if (result.ok) {
        set({ gameState: result.value, selectedCards: null, selectedFromPile: null });
        logger.debug("Karte aufgedeckt", { column: columnIndex });
        if (canAutoComplete(result.value)) get().startAutoComplete();
        else scheduleAutoAce(result.value);
      }
      return;
    }

    if (!card.faceUp) return;

    // Aufgedeckte Karte → Auto-Move zum besten Ziel
    const cardsToMove = column.slice(cardIndex);
    if (cardsToMove.length > 0 && cardsToMove.every((c) => c.faceUp)) {
      autoMove({ kind: "tableau", columnIndex }, cardsToMove);
    }
  },

  handleEmptyTableauClick: (columnIndex) => {
    const { gameState, selectedCards, selectedFromPile, clearSelection } = get();
    if (!selectedCards || !selectedFromPile) return;

    const firstCard = selectedCards[0];
    if (!firstCard || firstCard.rank !== 13) {
      clearSelection();
      return;
    }

    const result = applyMove(gameState, {
      type: "MOVE_TO_TABLEAU",
      cards: selectedCards,
      fromPile: selectedFromPile,
      toColumnIndex: columnIndex,
    });
    if (result.ok) {
      flyMove(selectedCards.map((c) => c.id), () =>
        set({ gameState: result.value, selectedCards: null, selectedFromPile: null }));
      if (canAutoComplete(result.value)) get().startAutoComplete();
      else scheduleAutoAce(result.value);
    } else {
      clearSelection();
    }
  },

  handleWasteClick: () => {
    const { gameState } = get();
    const topWaste = gameState.waste[gameState.waste.length - 1];
    if (!topWaste) return;
    console.log(`[handleWasteClick] card=${topWaste.rank} of ${topWaste.suit}`);

    // Waste-Karte sofort zum besten Ziel bewegen
    autoMove({ kind: "waste" }, [topWaste]);
  },

  handleFoundationClick: (foundationIndex) => {
    const { gameState, selectedCards, selectedFromPile, clearSelection } = get();
    if (!selectedCards || !selectedFromPile || selectedCards.length !== 1) {
      clearSelection();
      return;
    }

    const card = selectedCards[0]!;
    const fp = gameState.foundation[foundationIndex];
    if (!fp) return;

    if (canPlaceOnFoundation(card, fp, FOUNDATION_SUIT_ORDER[foundationIndex]!)) {
      const result = applyMove(gameState, {
        type: "MOVE_TO_FOUNDATION",
        card,
        fromPile: selectedFromPile,
        toFoundationIndex: foundationIndex,
      });
      if (result.ok) {
        flyMove([card.id], () =>
          set({ gameState: result.value, selectedCards: null, selectedFromPile: null }));
        logger.debug("Auf Foundation gelegt", { suit: card.suit, rank: card.rank });
        if (canAutoComplete(result.value)) get().startAutoComplete();
        else scheduleAutoAce(result.value);
      }
    } else {
      clearSelection();
    }
  },

  undo: () => {
    const { gameState } = get();
    const limit = GAME_CONFIG.maxUndoCount;
    if (limit > 0 && gameState.undoCount >= limit) {
      logger.info("Undo-Limit erreicht", { limit });
      return;
    }
    const result = undoLastMove(gameState);
    if (result.ok) {
      set({ gameState: result.value, selectedCards: null, selectedFromPile: null });
      logger.debug("Undo", { undoCount: result.value.undoCount });
    }
  },

  applyDrop: (toTarget: string) => {
    const drag = dragState.get();
    dragState.clear();
    if (!drag) return false;

    const { gameState } = get();
    const { cards, fromPile } = drag;

    if (toTarget.startsWith("tableau-")) {
      const colIndex = parseInt(toTarget.replace("tableau-", ""), 10);
      const column = gameState.tableau[colIndex];
      if (!column) return false;

      const valid =
        cards.length === 1
          ? canPlaceOnTableau(cards[0]!, column)
          : canPlaceSequenceOnTableau(cards, column);
      if (!valid) return false;

      const result = applyMove(gameState, {
        type: "MOVE_TO_TABLEAU",
        cards,
        fromPile,
        toColumnIndex: colIndex,
      });
      if (result.ok) {
        set({ gameState: result.value, selectedCards: null, selectedFromPile: null });
        if (canAutoComplete(result.value)) get().startAutoComplete();
        else scheduleAutoAce(result.value);
        return true;
      }
    } else if (toTarget.startsWith("foundation-")) {
      const foundIndex = parseInt(toTarget.replace("foundation-", ""), 10);
      if (cards.length !== 1) return false;
      const card = cards[0]!;
      const fp = gameState.foundation[foundIndex];
      if (!fp) return false;
      if (!canPlaceOnFoundation(card, fp, FOUNDATION_SUIT_ORDER[foundIndex]!)) return false;

      const result = applyMove(gameState, {
        type: "MOVE_TO_FOUNDATION",
        card,
        fromPile,
        toFoundationIndex: foundIndex,
      });
      if (result.ok) {
        set({ gameState: result.value, selectedCards: null, selectedFromPile: null });
        if (canAutoComplete(result.value)) get().startAutoComplete();
        else scheduleAutoAce(result.value);
        return true;
      }
    }
    return false;
  },

  startAutoComplete: () => {
    const { isAutoCompleting } = get();
    if (isAutoCompleting) return;
    set({ isAutoCompleting: true, selectedCards: null, selectedFromPile: null });
    logger.info("Auto-Complete gestartet");

    const delay = GAME_CONFIG.autoCompleteDelayMs;
    let retryCount = 0;
    const MAX_RETRIES = 5;

    function runStep() {
      const { gameState, isAutoCompleting } = get();
      if (!isAutoCompleting || gameState.status === "won") {
        set({ isAutoCompleting: false });
        return;
      }
      const move = findAutoCompleteMove(gameState);
      if (!move) {
        // Kein Zug gefunden — aber Spiel noch nicht gewonnen?
        // Mögliche Ursache: Race Condition in der Fly-Animation.
        // flyingIds bereinigen und erneut versuchen.
        if (retryCount < MAX_RETRIES) {
          retryCount++;
          logger.warn(`Auto-Complete: kein Zug gefunden, Retry ${retryCount}/${MAX_RETRIES}`);
          // flyingIds erzwungen aufräumen (macht evtl. unsichtbare Karten wieder sichtbar)
          const cbs = get()._flyCallbacks;
          if (cbs) { cbs.onBeforeMove([]); cbs.onAfterMove(); }
          setTimeout(runStep, delay * 3);
          return;
        }
        logger.error("Auto-Complete: abgebrochen nach max Retries, Spiel nicht gewonnen");
        set({ isAutoCompleting: false });
        return;
      }
      retryCount = 0;
      const result = applyMove(gameState, move);
      if (result.ok) {
        // Fly-Animation mit Victory-Speed für jede Karte
        const card = (move as { card?: { id?: string } }).card;
        if (card?.id) {
          flyMove([card.id], () => set({ gameState: result.value }), { speedType: "victory" });
        } else {
          set({ gameState: result.value });
        }
        setTimeout(runStep, delay);
      } else {
        set({ isAutoCompleting: false });
      }
    }

    setTimeout(runStep, delay);
  },

  tick: () => {
    set((state) => {
      if (state.gameState.status === "playing") {
        return {
          gameState: {
            ...state.gameState,
            elapsedSeconds: state.gameState.elapsedSeconds + 1,
          },
        };
      }
      return state;
    });
  },
  }; // end return
}); // end create
